"use client"

import { signIn, getSession } from "next-auth/react"
import { useState, useEffect, useCallback } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import Link from "next/link"
import {
  Shield,
  Lock,
  Building2,
  Handshake,
  BadgeCheck,
  ArrowRight,
  Mail,
  Eye,
  EyeOff,
  Sparkles,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { ThemeToggle } from "@/components/theme-toggle"

type UserRole = 'merchant' | 'partner'

const roleContent = {
  merchant: {
    signInTitle: "Welcome back",
    signInSubtitle: "Sign in to your merchant dashboard",
  },
  partner: {
    signInTitle: "Partner Portal",
    signInSubtitle: "Sign in to track your earnings",
  }
}

export default function SignInPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [credentialsLoading, setCredentialsLoading] = useState(false)
  const [error, setError] = useState("")
  const [unverifiedEmail, setUnverifiedEmail] = useState("")
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState("")
  const [selectedRole, setSelectedRole] = useState<UserRole>('merchant')
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') || '/'

  const content = roleContent[selectedRole]

  useEffect(() => {
    getSession().then((session) => {
      if (session) {
        router.push(callbackUrl)
      }
    })
  }, [router, callbackUrl])

  const handleRoleChange = useCallback((role: UserRole) => {
    setSelectedRole(role)
  }, [])

  const handleCredentialsSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setCredentialsLoading(true)
    setError("")
    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      })
      if (result?.error === "EMAIL_NOT_VERIFIED") {
        setUnverifiedEmail(email)
        setError("EMAIL_NOT_VERIFIED")
      } else if (result?.error === "ACCOUNT_DISABLED") {
        setError("ACCOUNT_DISABLED")
      } else if (result?.error) {
        setError("Invalid email or password. Please try again.")
      } else if (result?.ok) {
        router.push(callbackUrl)
      }
    } catch (err) {
      console.error('SignIn error:', err)
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setCredentialsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    setError("")
    try {
      await signIn('google', { callbackUrl, redirect: true })
    } catch (err) {
      console.error('SignIn error:', err)
      setError("An unexpected error occurred. Please try again.")
      setIsLoading(false)
    }
  }

  const handleResendVerification = async () => {
    setResendLoading(true)
    setResendMessage("")
    try {
      await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail }),
      })
      setResendMessage("Verification email sent. Please check your inbox.")
    } catch {
      setResendMessage("Failed to resend. Please try again.")
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-fintech text-foreground lg:grid lg:grid-cols-[1.05fr_.95fr]">
      <header className="absolute inset-x-0 top-0 z-30 flex items-center justify-between p-5 sm:p-8">
        <Link href="/" className="brand-glass flex items-center gap-2.5 rounded-xl py-2 pl-2 pr-4 transition hover:-translate-y-0.5">
          <img src="/favicon.svg" alt="FluxPay" className="h-9 w-9" />
          <span className="text-sm font-bold uppercase tracking-[0.14em]">FluxPay</span>
        </Link>
        <ThemeToggle variant="segmented" />
      </header>

      <aside className="relative hidden overflow-hidden bg-[linear-gradient(145deg,#111a3f_0%,#172554_42%,#20113e_100%)] p-12 text-white lg:flex lg:min-h-screen lg:flex-col lg:justify-end xl:p-16">
        <div className="absolute -left-32 top-10 h-[520px] w-[520px] rounded-full bg-[#3b82f6]/30 blur-[120px]" />
        <div className="absolute -bottom-36 -right-24 h-[560px] w-[560px] rounded-full bg-[#8b5cf6]/30 blur-[110px]" />
        <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,.18)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.18)_1px,transparent_1px)] [background-size:72px_72px]" />
        <div className="relative max-w-2xl pb-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] backdrop-blur-xl"><Sparkles className="h-4 w-4" /> Secure access</span>
          <h2 className="mt-7 text-6xl font-semibold uppercase leading-[0.82] tracking-[-0.07em] xl:text-7xl">Your payment<br /><span className="text-white/40">control plane.</span></h2>
          <p className="mt-8 max-w-lg text-lg leading-8 text-white/70">Manage provider connectivity, routing, transactions and settlements from one operational workspace.</p>
          <div className="mt-10 grid grid-cols-3 gap-3 text-xs font-medium text-white/70">
            {["Unified routing", "Secure access", "Live operations"].map((item) => <div key={item} className="rounded-2xl border border-white/15 bg-white/[0.07] px-4 py-4 backdrop-blur-xl">{item}</div>)}
          </div>
        </div>
      </aside>

      <section className="relative flex min-h-screen items-center justify-center px-5 pb-10 pt-28 sm:px-8 lg:pt-24">
      {/* Subtle background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={cn(
          "absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full blur-[150px] transition-all duration-700",
          "bg-[#3b82f6]/12"
        )} />
      </div>

      <div className="w-full max-w-[440px] relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8 lg:hidden">
          <div className="flex items-center gap-2">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="FluxPay" className="w-10 h-10" />
            <span className="text-2xl font-bold uppercase tracking-[0.14em]">FluxPay</span>
          </div>
        </div>

        {/* Role Selector */}
        <div className="flex justify-center mb-8">
          <div className="relative p-1.5 rounded-2xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.08]">
            {/* Sliding background */}
            <div className={cn(
              "absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] rounded-xl transition-all duration-500 ease-out",
              selectedRole === 'merchant'
                ? "left-1.5 bg-gradient-to-r from-[#2563eb] to-[#06b6d4] shadow-lg shadow-[#3b82f6]/25"
                : "left-[calc(50%+3px)] bg-gradient-to-r from-[#3b82f6] to-[#8b5cf6] shadow-lg shadow-[#8b5cf6]/25"
            )} />

            <div className="relative flex">
              <button
                onClick={() => handleRoleChange('merchant')}
                className={cn(
                  "flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 w-[140px]",
                  selectedRole === 'merchant' ? "text-white" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Building2 className="w-4 h-4" />
                <span>Merchant</span>
              </button>
              <button
                onClick={() => handleRoleChange('partner')}
                className={cn(
                  "flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 w-[140px]",
                  selectedRole === 'partner' ? "text-white" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Handshake className="w-4 h-4" />
                <span>Partner</span>
              </button>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 tracking-tight">
            {content.signInTitle}
          </h1>
          <p className="text-muted-foreground">
            {content.signInSubtitle}
          </p>
        </div>

        {/* Sign in card */}
        <div className="brand-glass relative overflow-hidden rounded-3xl p-8 dark:bg-white/[0.04]">
          <div className={cn(
            "absolute inset-0 rounded-3xl opacity-50 transition-all duration-500",
            selectedRole === 'merchant'
              ? "bg-gradient-to-br from-[#3b82f6]/12 via-transparent to-[#06b6d4]/6"
              : "bg-gradient-to-br from-[#8b5cf6]/12 via-transparent to-[#3b82f6]/6"
          )} />

          <div className="relative">
            {error === "ACCOUNT_DISABLED" ? (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-sm text-red-400">
                  Your account has been suspended. Please contact support.
                </p>
              </div>
            ) : error === "EMAIL_NOT_VERIFIED" ? (
              <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-amber-400 mb-2">
                  Please verify your email before signing in.
                </p>
                {resendMessage ? (
                  <p className="text-sm text-green-400">{resendMessage}</p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="text-sm text-amber-300 hover:text-amber-200 underline underline-offset-2 transition-colors disabled:opacity-50"
                  >
                    {resendLoading ? "Sending..." : "Resend verification email"}
                  </button>
                )}
              </div>
            ) : error ? (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            ) : null}

            {/* Email/Password Form */}
            <form onSubmit={handleCredentialsSignIn} className="space-y-4 mb-6">
              <div>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 w-full rounded-xl border border-input bg-background/70 pl-11 pr-4 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-white/[0.035]"
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 w-full rounded-xl border border-input bg-background/70 pl-11 pr-11 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-white/[0.035]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <a
                  href="/auth/forgot-password"
                  className="text-sm text-muted-foreground transition-colors hover:text-blue-500 dark:hover:text-cyan-300"
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                disabled={credentialsLoading}
                className={cn(
                  "w-full h-12 flex items-center justify-center gap-2 rounded-xl font-semibold text-sm transition-all duration-300 disabled:opacity-50 hover:-translate-y-0.5",
                  selectedRole === 'merchant'
                    ? "bg-gradient-to-r from-[#2563eb] to-[#06b6d4] text-white shadow-lg shadow-[#3b82f6]/20"
                    : "bg-gradient-to-r from-[#3b82f6] to-[#8b5cf6] text-white shadow-lg shadow-[#8b5cf6]/20"
                )}
              >
                {credentialsLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border dark:border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-3 text-muted-foreground">or continue with</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="brand-inverse-action group relative flex h-14 w-full items-center justify-center gap-3 rounded-2xl font-semibold shadow-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98] disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>Continue with Google</span>
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Sign up link */}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <a href="/auth/register" className={cn(
            "font-semibold text-blue-600 transition-colors hover:text-blue-700 dark:text-cyan-300 dark:hover:text-cyan-200"
          )}>
            Sign up
          </a>
        </p>

        {/* Trust indicators */}
        <div className="mt-6 flex items-center justify-center gap-6">
          {[
            { icon: Shield, label: 'FINTRAC MSB' },
            { icon: Lock, label: '256-bit SSL' },
            { icon: BadgeCheck, label: 'Bank-Grade' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-1.5 text-muted-foreground">
              <item.icon className="w-4 h-4" />
              <span className="text-xs">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Terms */}
        <p className="mt-5 text-center text-xs text-gray-600">
          By signing in, you agree to our{" "}
          <a href="/terms" className="text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">Terms</a>
          {" "}and{" "}
          <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">Privacy Policy</a>
        </p>
      </div>
      </section>
    </div>
  )
}
